/**
 * FirmaTurleriPage.jsx — Sistem Ayarları › Firma Türleri (backlog #159, 2026-10-11)
 * Route: /Management/SistemAyarlari/FirmaTurleri  (yalnızca personel; yazma uçları da öyle, #154)
 *
 * firma_type kataloğu: Katılımcı, Organizatör, Dekoratör, Taşeron ve alt türleri (Nakliye, Zemin, Temizlik…).
 * Bilge'nin kararı: ekle + yeniden adlandır + güvenli sil.
 *   - Kodun id ile okuduğu türler (1, 2, 3, 4 ve fuar hizmet türleri 7, 11, 12, 13, 14) yalnızca yeniden
 *     adlandırılır: üst türleri değişmez, silinmez.
 *   - Bir firma–fuar, firma–stand, taşeron, atama talebi, form, belge izni satırı ya da alt türü olan tür
 *     silinmez. Sayfa `silinebilir` / `koddaKullanilan` bayraklarını sunucudan okur; kuralı backend uygular.
 *   - Yeni türün id'si 4'ten büyük olur; kod 4 ve üstünü TAŞERON sayar (FirmaTypeConstant.isTaseron), üst
 *     türü ne olursa olsun. Formda yazıyor.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Card, CardBody, CardHeader, Table, Button, Badge, Alert, Spinner, Modal, ModalHeader, ModalBody,
  ModalFooter, Label, Input, Row, Col,
} from "reactstrap";
import { Plus, Pencil, Trash2, Lock, Info } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  getFirmaTurleri, createFirmaTuru, updateFirmaTuru, deleteFirmaTuru,
} from "../../../services/veriAyarlariService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

const BOS_FORM = { firmaTypeValue: "", adTr: "", adEn: "", parentId: 4 };

const KULLANIM_ETIKETLERI = [
  ["firmaToFuar", "firma–fuar"],
  ["firmaToStand", "firma–stand"],
  ["firmaTaseron", "taşeron bağı"],
  ["atamaTalebi", "atama talebi"],
  ["isKazasiFormu", "iş kazası formu"],
  ["fuarBelgeTipi", "fuar belge tipi"],
  ["kisiBelgeIzni", "kişi belge izni"],
  ["altTipSayisi", "alt tür"],
];

function kullanimOzeti(row) {
  const parcalar = KULLANIM_ETIKETLERI
    .filter(([k]) => Number(row[k]) > 0)
    // WAS: .map(([k, ad]) => `${Number(row[k]).toLocaleString("tr-TR")} ${ad}`);
    // #349: the labels above stay as the Turkish defaults; the summary translates them by key when it is built.
    .map(([k, ad]) => `${Number(row[k]).toLocaleString(currentLocaleTag())} ${i18n.t(`ayarlar:firmaTurleri.kullanimEtiketleri.${k}`, { defaultValue: ad })}`);
  return parcalar.length ? parcalar.join(" · ") : i18n.t("ayarlar:firmaTurleri.kullanilmiyor");
}

function TurFormu({ form, setForm, turler, duzenlenen, kaydediyor }) {
  const { t: tx } = useTranslation("ayarlar");
  const sabit = duzenlenen?.koddaKullanilan === true;
  // Bir tür kendisinin ya da alt türünün altına taşınamaz: backend de reddeder, burada listeden çıkar.
  const altTurler = useMemo(() => {
    if (!duzenlenen) return new Set();
    const out = new Set([duzenlenen.firmaTypeId]);
    let degisti = true;
    while (degisti) {
      degisti = false;
      for (const t of turler) {
        if (out.has(t.parentId) && !out.has(t.firmaTypeId)) { out.add(t.firmaTypeId); degisti = true; }
      }
    }
    return out;
  }, [duzenlenen, turler]);

  return (
    <>
      <Row className="g-2">
        <Col md={6}>
          <Label className="small mb-1" for="ft-tr">{tx("firmaTurleri.turkceAd")}</Label>
          <Input id="ft-tr" bsSize="sm" value={form.adTr} maxLength={100} disabled={kaydediyor}
                 onChange={(e) => setForm((f) => ({ ...f, adTr: e.target.value }))} />
        </Col>
        <Col md={6}>
          <Label className="small mb-1" for="ft-en">{tx("firmaTurleri.ingilizceAd")}</Label>
          <Input id="ft-en" bsSize="sm" value={form.adEn} maxLength={100} disabled={kaydediyor}
                 onChange={(e) => setForm((f) => ({ ...f, adEn: e.target.value }))} />
        </Col>
        <Col md={6}>
          <Label className="small mb-1" for="ft-value">{tx("firmaTurleri.kayitAdiFirmaTypeValue")}</Label>
          <Input id="ft-value" bsSize="sm" value={form.firmaTypeValue} maxLength={100} disabled={kaydediyor}
                 placeholder={tx("firmaTurleri.bossaTurkceAdKullanilir")}
                 onChange={(e) => setForm((f) => ({ ...f, firmaTypeValue: e.target.value }))} />
        </Col>
        <Col md={6}>
          <Label className="small mb-1" for="ft-parent">{tx("firmaTurleri.ustTur")}</Label>
          <Input id="ft-parent" type="select" bsSize="sm" value={form.parentId} disabled={kaydediyor || sabit}
                 onChange={(e) => setForm((f) => ({ ...f, parentId: Number(e.target.value) }))}>
            <option value={0}>{tx("firmaTurleri.anaTur")}</option>
            {turler.filter((t) => !altTurler.has(t.firmaTypeId)).map((t) => (
              <option key={t.firmaTypeId} value={t.firmaTypeId}>
                {t.firmaTypeId} · {t.adTr || t.firmaTypeValue}
              </option>
            ))}
          </Input>
        </Col>
      </Row>
      {sabit ? (
        <Alert color="secondary" className="small py-2 mt-3 mb-0 d-flex gap-2">
          <Lock size={14} className="mt-1 flex-shrink-0" />
          {tx("firmaTurleri.buTurKoddaKullaniliyorAdini")}
        </Alert>
      ) : !duzenlenen && (
        <Alert color="info" className="small py-2 mt-3 mb-0 d-flex gap-2">
          <Info size={14} className="mt-1 flex-shrink-0" />
          {tx("firmaTurleri.yeniTurunNumarasi4Ten")}
        </Alert>
      )}
    </>
  );
}

export default function FirmaTurleriPage() {
  const { t: tx } = useTranslation("ayarlar");
  useEffect(() => { document.title = i18n.t("ayarlar:firmaTurleri.firmaTurleriFds"); }, []);

  const [turler, setTurler] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [bilgi, setBilgi] = useState(null);
  const [yenile, setYenile] = useState(0);

  const [modal, setModal] = useState(null); // { duzenlenen: row | null }
  const [form, setForm] = useState(BOS_FORM);
  const [kaydediyor, setKaydediyor] = useState(false);
  const [modalHata, setModalHata] = useState(null);
  const [silinecek, setSilinecek] = useState(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      try {
        const rows = await getFirmaTurleri();
        if (!ignore) setTurler(rows);
      } catch (e) {
        if (!ignore) setHata(e.message);
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [yenile]);

  function ac(duzenlenen) {
    setModalHata(null);
    setForm(duzenlenen ? {
      firmaTypeValue: duzenlenen.firmaTypeValue ?? "",
      adTr: duzenlenen.adTr ?? "",
      adEn: duzenlenen.adEn ?? "",
      parentId: duzenlenen.parentId ?? 0,
    } : BOS_FORM);
    setModal({ duzenlenen });
  }

  async function kaydet() {
    const adTr = form.adTr.trim();
    const value = form.firmaTypeValue.trim() || adTr;
    if (!value) { setModalHata(tx("firmaTurleri.turkceAdYaDaKayit")); return; }
    setKaydediyor(true);
    setModalHata(null);
    try {
      const govde = { ...form, firmaTypeValue: value };
      if (modal.duzenlenen) await updateFirmaTuru(modal.duzenlenen.firmaTypeId, govde);
      else await createFirmaTuru(govde);
      setBilgi(modal.duzenlenen ? tx("firmaTurleri.firmaTuruGuncellendi") : tx("firmaTurleri.firmaTuruEklendi"));
      setModal(null);
      setYenile((n) => n + 1);
    } catch (e) {
      setModalHata(e.message);
    } finally {
      setKaydediyor(false);
    }
  }

  async function sil() {
    setKaydediyor(true);
    setModalHata(null);
    try {
      await deleteFirmaTuru(silinecek.firmaTypeId);
      setBilgi(tx("firmaTurleri.silindi", { v: silinecek.adTr || silinecek.firmaTypeValue }));
      setSilinecek(null);
      setYenile((n) => n + 1);
    } catch (e) {
      setModalHata(e.message);
    } finally {
      setKaydediyor(false);
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("firmaTurleri.firmaTurleri")} pageTitle={tx("firmaTurleri.sistemAyarlari")} />
        {hata && <Alert color="danger">{hata}</Alert>}
        {bilgi && <Alert color="success" toggle={() => setBilgi(null)}>{bilgi}</Alert>}
        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h5 className="mb-0">{tx("firmaTurleri.firmaTurleri")}</h5>
              <small className="text-muted">
                {tx("firmaTurleri.firmaninFuardakiVeStanddakiRolu")}
              </small>
            </div>
            <Button color="primary" size="sm" onClick={() => ac(null)} disabled={yukleniyor}>
              <Plus size={14} className="me-1" />{tx("firmaTurleri.yeniFirmaTuru")}
            </Button>
          </CardHeader>
          <CardBody>
            {yukleniyor ? <Spinner size="sm" /> : (
              <div className="table-responsive">
                <Table size="sm" hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>#</th><th>{tx("firmaTurleri.turkceAd")}</th><th>{tx("firmaTurleri.ingilizceAd")}</th><th>{tx("firmaTurleri.ustTur")}</th><th>{tx("firmaTurleri.kullanim")}</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {turler.map((t) => (
                      <tr key={t.firmaTypeId}>
                        <td className="text-muted">{t.firmaTypeId}</td>
                        <td>
                          {t.adTr || <span className="text-muted fst-italic">{t.firmaTypeValue}</span>}
                          {t.koddaKullanilan && <Badge color="secondary" className="ms-2" title={tx("firmaTurleri.kodBuNumarayiOkur")}><Lock size={10} />{" "}{tx("firmaTurleri.kodda")}</Badge>}
                          {t.taseronSayilir && !t.koddaKullanilan && <Badge color="light" className="text-muted ms-2">{tx("firmaTurleri.taseronSayilir")}</Badge>}
                        </td>
                        <td>{t.adEn || <span className="text-muted">—</span>}</td>
                        <td>{t.parentId ? `${t.parentId} · ${t.parentValue ?? ""}` : <span className="text-muted">{tx("firmaTurleri.anaTur2")}</span>}</td>
                        <td className="small text-muted">{kullanimOzeti(t)}</td>
                        <td className="text-end text-nowrap">
                          <Button size="sm" color="light" className="me-1" onClick={() => ac(t)} title={tx("common:duzenle")}>
                            <Pencil size={14} />
                          </Button>
                          <Button size="sm" color="light" disabled={!t.silinebilir}
                                  title={t.silinebilir ? tx("common:sil") : t.koddaKullanilan ? tx("firmaTurleri.koddaKullaniliyorSilinemez") : tx("firmaTurleri.kullanimdaSilinemez")}
                                  onClick={() => { setModalHata(null); setSilinecek(t); }}>
                            <Trash2 size={14} className={t.silinebilir ? "text-danger" : "text-muted"} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>

      <Modal isOpen={!!modal} toggle={() => !kaydediyor && setModal(null)}>
        <ModalHeader toggle={() => !kaydediyor && setModal(null)}>
          {modal?.duzenlenen ? tx("firmaTurleri.firmaTuru", { firmaTypeId: modal.duzenlenen.firmaTypeId }) : tx("firmaTurleri.yeniFirmaTuru")}
        </ModalHeader>
        <ModalBody>
          {modalHata && <Alert color="danger" className="small py-2">{modalHata}</Alert>}
          {modal && <TurFormu form={form} setForm={setForm} turler={turler} duzenlenen={modal.duzenlenen} kaydediyor={kaydediyor} />}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" disabled={kaydediyor} onClick={() => setModal(null)}>{tx("firmaTurleri.vazgec")}</Button>
          <Button color="primary" size="sm" disabled={kaydediyor} onClick={kaydet}>
            {kaydediyor ? <Spinner size="sm" /> : tx("common:kaydet")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!silinecek} toggle={() => !kaydediyor && setSilinecek(null)}>
        <ModalHeader toggle={() => !kaydediyor && setSilinecek(null)}>{tx("firmaTurleri.firmaTurunuSil")}</ModalHeader>
        <ModalBody>
          {modalHata && <Alert color="danger" className="small py-2">{modalHata}</Alert>}
          <p className="mb-0">
            <strong>{silinecek?.adTr || silinecek?.firmaTypeValue}</strong>{" "}{tx("firmaTurleri.kaliciOlarakSilinecekHicbirKayitta", { firmaTypeId: silinecek?.firmaTypeId })}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" disabled={kaydediyor} onClick={() => setSilinecek(null)}>{tx("firmaTurleri.vazgec")}</Button>
          <Button color="danger" size="sm" disabled={kaydediyor} onClick={sil}>
            {kaydediyor ? <Spinner size="sm" /> : tx("common:sil")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
